import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  NgControl,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  ControlsOf,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  ValuesOf,
} from '@ngneat/reactive-forms';
import { isAfter, isValid } from 'date-fns';
import * as _ from 'lodash';
import { BehaviorSubject, debounceTime, merge, Observable } from 'rxjs';
import { map, scan, take, takeUntil, throttleTime } from 'rxjs/operators';

import { Attachment } from '@digital-platform/attachments/domain';
import { GeneratorsFacadeService } from '@digital-platform/generators/services';
import {
  FormValidators,
  NestedFormComponent,
} from '@digital-platform/shared/ui';
import { SignNowDocument } from '@digital-platform/sign-now/domain';
import { User } from '@digital-platform/users/domain';
import { LoginSessionFacadeService } from '@digital-platform/users/services';
import {
  AdditionalSupplement,
  ChangeRequestDetails,
  CommentLogHistory,
  InternalStatus,
  LabAnalyticalAttachment,
  RecertificationDetails,
  SafetyDataSheet,
  SpecialWasteProfile,
  SpecialWasteProfileModel,
  WasteProfile,
  WasteProfileCustomerCard,
  WasteProfileCardTypes,
  WasteStreamOptions,
  minAndMaxDate,
  resetBadRawDateInput,
} from '@digital-platform/waste-profiles/domain';
import {
  WasteProfileCardsService,
  WasteProfilesFacadeService,
} from '@digital-platform/waste-profiles/services';

type MetatdataFormElements = Pick<
  WasteProfile,
  | 'active'
  | 'expiryDate'
  | 'truxMaterialDescription'
  | 'logbookSequenceNumber'
  | 'yearCode'
  | 'coordinator'
  | 'destination'
  | 'logbookNotes'
  | 'name'
  | 'generator'
  | 'signNowDocUrl'
  | 'signNowDocument'
  | 'internalStatus'
  | 'approverName'
  | 'commentLog'
  | 'pendingChangeRequest'
  | 'pendingRecertification'
  | 'approvalNotes'
  | 'number'
  | 'additionalAdminSupplements'
  | 'stateInternalAdminAttachments'
  | 'internalAdminAttachments'
  | 'stateApprovalWasteCodeNotApplicable'
> &
  Partial<
    Pick<
      SpecialWasteProfile,
      | 'disposalConsideration'
      | 'approvedDisposalConsideration'
      | 'isTransferFacilityRequired'
      | 'metadataCharacteristics'
      | 'labAnalyticalAttachments'
      | 'safetyDataSheets'
      | 'additionalSupplements'
      | 'stateApprovalWasteCode'
      | 'estimatedVolume'
      | 'unitOfMeasure'
      | 'thirdPartyAuthAdminAttachment'
    >
  > & {
    salesRep: {
      name: string;
      number: string;
    };
  };

type InputData = Pick<WasteProfile, 'salesRepName' | 'salesRepNumber'> &
  Omit<MetatdataFormElements, 'salesRep'>;

type MetadataFormControls = ControlsOf<
  Omit<
    MetatdataFormElements,
    | 'expiryDate'
    | 'coordinator'
    | 'salesRep'
    | 'destination'
    | 'generator'
    | 'signNowDocument'
    | 'commentLog'
    | 'pendingChangeRequest'
    | 'pendingRecertification'
    | 'labAnalyticalAttachments'
    | 'safetyDataSheets'
    | 'additionalSupplements'
    | 'stateApprovalWasteCodeNotApplicable'
    | 'thirdPartyAuthAdminAttachment'
  >
> & {
  expiryDate: FormControl<Date>;
  coordinator: FormControl<User>;
  salesRep: FormControl<{
    name: string;
    number: string;
  }>;
  destination: FormGroup<
    ControlsOf<{
      landfillname: string;
      divisionNumber: string;
    }>
  >;
  generator: FormGroup<
    ControlsOf<{
      name: string;
      generatorAddress: string;
      city: string;
      state: string;
    }>
  >;
  signNowDocument: FormControl<SignNowDocument>;
  commentLog: FormControl<CommentLogHistory[]>;
  pendingChangeRequest: FormControl<ChangeRequestDetails>;
  pendingRecertification: FormControl<RecertificationDetails>;
  labAnalyticalAttachments: FormControl<LabAnalyticalAttachment[]>;
  thirdPartyAuthAdminAttachment: FormControl<Attachment>;
  safetyDataSheets: FormControl<SafetyDataSheet[]>;
  additionalSupplements: FormControl<AdditionalSupplement[]>;
  stateApprovalWasteCodeNotApplicable: FormControl<boolean>;
};

interface StatusTransition {
  readonly state: InternalStatus;
  readonly options: InternalStatus[];
}

@Component({
  selector: 'digital-platform-update-waste-profile-metadata',
  templateUrl: './update-waste-profile-metadata.component.html',
  styleUrls: ['./update-waste-profile-metadata.component.scss'],
})
export class UpdateWasteProfileMetadataComponent
  extends NestedFormComponent<InputData, MetadataFormControls>
  implements OnChanges, OnInit, ControlValueAccessor<InputData>
{
  @Input() public wasteProfile: WasteProfile | null = null;
  @Output() public saveAttachments: EventEmitter<void> =
    new EventEmitter<void>();
  @Output() public deleteAttachments: EventEmitter<void> =
    new EventEmitter<void>();
  @Output() public checkWasteProfileNumberIsValid: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @Output() public submitWhenValid: EventEmitter<boolean> =
    new EventEmitter<boolean>();

  @Output() public submitActivate: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @ViewChild('activeWasteProfileDialog', { static: false })
  activeWasteProfileDialog;

  streamOptions: WasteStreamOptions = {
    options: [
      'Cubic Yards',
      'Tons',
      'Gallons',
      'Drums',
      'Pounds',
      'Pallets',
      'Totes',
    ],
  };
  public form: FormGroup<MetadataFormControls>;
  public signNowDocuments: SignNowDocument[] = [];
  public labAttachments: Attachment[] = [];
  public today = new Date();
  public safetyDataSheets: Attachment[] = [];
  public additionalSupplements: Attachment[] = [];
  public showAnotherAdditionalAdminSupplements = false;
  public showAnotherInternalOnlyAttachment = false;
  public showAnotherAdminStateAttachments = false;
  public attachmentsToSave = false;
  public attachmentsToDelete = false;
  public deleteFile: {
    attachment:
      | AdditionalSupplement
      | Attachment
      | { url: string; uploadDate: Date };
    attachmentName: string | undefined;
  };
  @Output() delete = new EventEmitter<WasteProfileCardTypes>();
  removeDialogRef: MatDialogRef<UpdateWasteProfileMetadataComponent>;
  public isValidWasteProfileNumber = true;
  isAnalyst = false;
  disableGeneratorFields = true;
  generator: any;
  internalStatusOptions: string[];
  states: StatusTransition[];
  activeTitle = 'inactive';
  activeWPState = 'Active';
  canActivate = false;
  public formState: any;
  wasteProfileCustomerCards$: Observable<WasteProfileCustomerCard[]>;
  public originalWasteProfileNumber: string;
  private wasteProfileCardsService = inject(WasteProfileCardsService);

  constructor(
    private fb: FormBuilder,
    @Optional() @Self() public controlDir: NgControl,
    private readonly facade: WasteProfilesFacadeService,

    private readonly generatorsFacade: GeneratorsFacadeService,
    private loginSessionFacade: LoginSessionFacadeService,
    public dialog: MatDialog
  ) {
    super(controlDir);
    this.loginSessionFacade.loginSession$.subscribe((loginSession) => {
      // if a user has this permission, then they are an analyst
      this.isAnalyst = loginSession.user.permissions.includes(
        'analyst_update:waste_profile'
      );

      // if a user has this permission, then they can see activate checkbox
      this.canActivate = loginSession.user.permissions.includes(
        'update:waste_profile_metadata'
      );

      // if a user has this permission, then they can edit generator
      const hasGeneratorPermission = loginSession.user.permissions.includes(
        'analyst_update:metadata'
      );
      if (hasGeneratorPermission) {
        this.disableGeneratorFields = false;
      } else {
        this.disableGeneratorFields = true;
      }
    });
    this.wasteProfileCustomerCards$ =
      this.wasteProfileCardsService.statusCards$;
  }

  // Imported from shared location to avoid duplication
  minAndMaxDate = minAndMaxDate;
  resetBadRawDateInput = resetBadRawDateInput;

  disableGeneratorNameControl(disabled: boolean) {
    if (disabled && !this.isAnalyst) {
      (
        (this.form.controls.generator as FormGroup<any>).controls[
          'name'
        ] as FormControl<any>
      ).disable();
    } else {
      (
        (this.form.controls.generator as FormGroup<any>).controls[
          'name'
        ] as FormControl<any>
      ).enable();
    }
  }

  initializeForm(wasteProfile: ValuesOf<MetadataFormControls>) {
    if (wasteProfile.active) {
      this.activeWPState = 'Active';
    } else {
      this.activeWPState = 'Inactive';
    }
    this.getAttachmentUrls();
    this.getSignNowDocuments();
    this.form = this.fb.group({
      additionalAdminSupplements: this.fb.array([null]),
      stateInternalAdminAttachments: this.fb.array([null]),
      internalAdminAttachments: this.fb.array([null]),
      thirdPartyAuthAdminAttachment: null,
      internalStatus: [wasteProfile?.internalStatus],
      active: [wasteProfile?.active],
      stateApprovalWasteCodeNotApplicable:
        wasteProfile?.stateApprovalWasteCodeNotApplicable,
      coordinator: [
        wasteProfile?.coordinator,
        this.requiredOnInternalStatus([
          InternalStatus.COORDINATOR_REVIEW,
          InternalStatus.COORDINATOR_APPROVED,
          InternalStatus.PENDING_CUSTOMER_INFO,
        ]),
      ],
      number: [wasteProfile?.number],
      salesRep: [
        wasteProfile?.salesRep,
        this.requiredOnInternalStatus([
          InternalStatus.COORDINATOR_REVIEW,
          InternalStatus.COORDINATOR_APPROVED,
          InternalStatus.PENDING_CUSTOMER_INFO,
        ]),
      ],
      approverName: [
        wasteProfile?.approverName,
        this.requiredOnInternalStatus([
          InternalStatus.ANALYST_REVIEW,
          InternalStatus.ANALYST_APPROVED,
          InternalStatus.FINAL_APPROVAL,
          InternalStatus.REJECTED,
        ]),
      ],
      yearCode: [
        wasteProfile?.yearCode,
        this.requiredOnInternalStatus([
          InternalStatus.COORDINATOR_APPROVED,
          InternalStatus.ANALYST_APPROVED,
          InternalStatus.FINAL_APPROVAL,
          InternalStatus.REJECTED,
        ]),
      ],
      logbookSequenceNumber: [
        wasteProfile?.logbookSequenceNumber,
        this.requiredOnInternalStatus([
          InternalStatus.COORDINATOR_APPROVED,
          InternalStatus.ANALYST_APPROVED,
          InternalStatus.FINAL_APPROVAL,
          InternalStatus.REJECTED,
        ]),
      ],
      truxMaterialDescription: [
        wasteProfile?.truxMaterialDescription,
        this.requiredOnInternalStatus([InternalStatus.COORDINATOR_APPROVED]),
      ],
      expiryDate: [
        wasteProfile?.expiryDate,
        [
          this.requiredOnInternalStatus([
            InternalStatus.FINAL_APPROVAL,
            InternalStatus.REJECTED,
            InternalStatus.ANALYST_APPROVED,
          ]),
          this.finalApprovalExpiryDate(() => new Date()),
          this.minAndMaxDate(),
        ],
      ],
      destination: this.fb.group({
        landfillname: this.fb.control({
          value: wasteProfile?.destination?.landfillname,
          disabled: true,
        }),
        divisionNumber: this.fb.control({
          value: wasteProfile?.destination?.divisionNumber,
          disabled: true,
        }),
      }),
      name: this.fb.control({
        value: wasteProfile?.name,
        disabled: this.disableGeneratorFields,
      }),

      commentLog: this.fb.control({
        value: wasteProfile?.commentLog,
        disabled: true,
      }),
      approvalNotes: [wasteProfile?.approvalNotes],
      approvedDisposalConsideration: [
        wasteProfile.approvedDisposalConsideration
          ? wasteProfile.approvedDisposalConsideration
          : this.wasteProfile &&
            SpecialWasteProfileModel.isSpecialWaste(this.wasteProfile)
          ? this.wasteProfile.disposalConsideration
          : null,
      ],
      isTransferFacilityRequired: [wasteProfile?.isTransferFacilityRequired],
      metadataCharacteristics: this.fb.group({
        wetWaste: [wasteProfile?.metadataCharacteristics?.wetWaste || false],
        sulfur: [wasteProfile?.metadataCharacteristics?.sulfur || false],
        sulfurPpm: [wasteProfile?.metadataCharacteristics?.sulfurPpm || ''],
        pfas: [wasteProfile?.metadataCharacteristics?.pfas || false],
        pfasPpb: [wasteProfile?.metadataCharacteristics?.pfasPpb || ''],
        heatGeneration: [
          wasteProfile?.metadataCharacteristics?.heatGeneration || false,
        ],
        heatGenerationJg: [
          wasteProfile?.metadataCharacteristics?.heatGenerationJg || '',
        ],
        heatGenerationMetalOxide: [
          wasteProfile?.metadataCharacteristics?.heatGenerationMetalOxide || '',
        ],
        pcbs: [wasteProfile?.metadataCharacteristics?.pcbs || false],
        norm: [wasteProfile?.metadataCharacteristics?.norm || false],
        tenorm: [wasteProfile?.metadataCharacteristics?.tenorm || false],
        texasClass1: [
          wasteProfile?.metadataCharacteristics?.texasClass1 || false,
        ],
        californiaHazardous: [
          wasteProfile?.metadataCharacteristics?.californiaHazardous || false,
        ],
      }),

      labAnalyticalAttachments: this.fb.control({
        value: wasteProfile?.labAnalyticalAttachments,
        disabled: true,
      }),
      safetyDataSheets: this.fb.control({
        value: wasteProfile?.safetyDataSheets,
        disabled: true,
      }),
      additionalSupplements: this.fb.control({
        value: wasteProfile?.additionalSupplements,
        disabled: true,
      }),
      logbookNotes: [wasteProfile?.logbookNotes],
      stateApprovalWasteCode: this.fb.control({
        value: wasteProfile?.stateApprovalWasteCode,
        disabled: !this.isAnalyst,
      }),
      unitOfMeasure: this.fb.control(
        {
          value: wasteProfile?.unitOfMeasure,
          disabled: !this.isAnalyst,
        },
        FormValidators.requiredIf(() => this.isAnalyst)
      ),
      estimatedVolume: this.fb.control(
        {
          value: wasteProfile?.estimatedVolume,
          disabled: !this.isAnalyst,
        },
        this.isAnalyst
          ? [
              Validators.required,
              Validators.max(999999999),
              FormValidators.integer as ValidatorFn,
            ]
          : []
      ),
      generator: this.fb.group({
        name: this.fb.control(
          {
            value: wasteProfile?.generator?.name,
            disabled: !this.isAnalyst,
          },
          FormValidators.requiredIf(() => this.isAnalyst)
        ),
        generatorAddress: this.fb.control({
          value: wasteProfile?.generator?.generatorAddress,
          disabled: this.disableGeneratorFields,
        }),
        city: this.fb.control({
          value: wasteProfile?.generator?.city,
          disabled: this.disableGeneratorFields,
        }),
        state: this.fb.control({
          value: wasteProfile?.generator?.state,
          disabled: this.disableGeneratorFields,
        }),
      }),
      signNowDocument: this.fb.control({
        value: wasteProfile?.signNowDocument,
        disabled: true,
      }),
      signNowDocUrl: this.fb.control({
        value: wasteProfile.signNowDocUrl,
        disabled: true,
      }),
      pendingChangeRequest: this.fb.control({
        value: wasteProfile.pendingChangeRequest,
        disabled: true,
      }),
      pendingRecertification: this.fb.control({
        value: wasteProfile.pendingRecertification,
        disabled: true,
      }),
    });
    const wasteProfileNoControl = this.form.get('number');
    const divisionNumber = this.form.get('destination.divisionNumber')?.value;

    this.form
      .select((state) => {
        const { yearCode, logbookSequenceNumber } = state;

        return {
          yearCode,
          logbookSequenceNumber,
        };
      })
      .subscribe(({ yearCode, logbookSequenceNumber }) => {
        if (yearCode && logbookSequenceNumber && divisionNumber) {
          wasteProfileNoControl?.setValue(
            `${divisionNumber}${yearCode}${logbookSequenceNumber}`,
            { emitEvent: false }
          );
        } else {
          wasteProfileNoControl?.setValue(null, { emitEvent: false });
        }
      });

    // update field validations that rely on internal sttaus
    this.form
      .select((state) => state.internalStatus)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => {
        const controls = [
          'expiryDate',
          'truxMaterialDescription',
          'logbookSequenceNumber',
          'coordinator',
          'approverName',
          'salesRep',
          'yearCode',
        ] as const;

        controls.forEach((controlName) => {
          const control = this.form.get(controlName);
          control.markAllAsTouched();
          control.updateValueAndValidity();
        });
      });
  }

  checkWasteProfileNumber() {
    const wasteProfileNumber = this.form.get('number').value;
    const searchObj = [];
    searchObj.push({ key: 'number', value: wasteProfileNumber });
    if (
      wasteProfileNumber?.length >= 10 &&
      wasteProfileNumber?.length <= 12 &&
      wasteProfileNumber !== this.originalWasteProfileNumber
    ) {
      this.wasteProfileCustomerCards$
        .pipe(
          debounceTime(1500),
          takeUntil(this.destroyed$),
          map((wasteProfiles) => {
            const existingProfile = wasteProfiles.find(
              (wasteProfileCustomerCard) =>
                wasteProfileCustomerCard?.number === wasteProfileNumber
            );
            return existingProfile;
          })
        )
        .subscribe((existingProfile) => {
          if (existingProfile) {
            // waste profile number already exists
            this.isValidWasteProfileNumber = false;
            this.checkWasteProfileNumberIsValid.emit(false);
          } else {
            // waste profile number is valid
            this.isValidWasteProfileNumber = true;
            this.checkWasteProfileNumberIsValid.emit(true);
          }
        });
    } else {
      this.isValidWasteProfileNumber = true;
      this.checkWasteProfileNumberIsValid.emit(true);
    }
  }
  checkDuplicateWasteProfileNum(): boolean {
    const wasteProfileNumber = this.form.get('number').value;
    const searchObj = [];
    let isDuplicate = false;
    searchObj.push({ key: 'number', value: wasteProfileNumber });
    if (wasteProfileNumber?.length >= 10 && wasteProfileNumber?.length <= 12) {
      this.facade.search(searchObj).subscribe((value) => {
        if (
          value[0]?.number === wasteProfileNumber &&
          value[0]?.id !== this.wasteProfile.id
        ) {
          // waste profile number already exists
          isDuplicate = true;
          this.isValidWasteProfileNumber = false;
          this.checkWasteProfileNumberIsValid.emit(false);
        } else {
          // waste profile number is valid
          this.isValidWasteProfileNumber = true;
          this.checkWasteProfileNumberIsValid.emit(true);
          this.submitWhenValid.emit(true);
          isDuplicate = false;
        }
      });
    } else {
      this.isValidWasteProfileNumber = true;
      this.checkWasteProfileNumberIsValid.emit(true);
      this.submitWhenValid.emit(true);
    }
    return isDuplicate;
  }

  requiredOnInternalStatus(
    status: InternalStatus | InternalStatus[]
  ): ValidatorFn {
    const requiredOnStatuses = Array.isArray(status) ? status : [status];

    const validateFn: ValidatorFn = (control) => {
      if (!this.form) {
        // skips until the form is ready
        return null;
      }

      const internalStatus = this.form.get('internalStatus').value;

      if (requiredOnStatuses.includes(internalStatus)) {
        return Validators.required(control);
      }

      return null;
    };

    return validateFn;
  }

  finalApprovalExpiryDate<T extends Date>(predicate: () => Date): ValidatorFn {
    const validatorFn = (control: AbstractControl<T>) => {
      try {
        // if no expiry date value, then bail out
        // we have a different validator for required
        if (!control.value) return null;

        const expiryDate = new Date(control.value);
        const today = predicate();

        return !!today && isValid(expiryDate)
          ? null
          : { afterDate: { afterDate: today, actual: control.value } };
      } catch {
        // ignore any errors from attempting to call the predicate
        return null;
      }
    };

    return validatorFn as ValidatorFn;
  }

  getControl<T extends keyof MetadataFormControls>(
    formControlName: T
  ): MetadataFormControls[T] {
    return this.form.controls[formControlName];
  }

  ngOnInit(): void {
    let wasteProfile: InputData = SpecialWasteProfileModel.DEFAULT_CONFIG;

    this.setStates();

    if (this.value) {
      wasteProfile = this.value;
      this.originalWasteProfileNumber = this.value.number;
    }
    if (this.form) {
      this.form.patchValue(this.mapInputValue(wasteProfile), {
        emitEvent: false,
      });
    } else {
      this.initializeForm(this.mapInputValue(wasteProfile));
      this.getOptionsForInternalStatus();
      this.onDisposalConsideration();
      this.onMetadataCharacteristicsChange();
      this.onAdditionalAdminSupplementUpdate();
    }
    //grab the latest generator for potential update
    if (this.wasteProfile?.generator?.id) {
      this.generatorsFacade
        .findById(this.wasteProfile.generator.id)
        .pipe(takeUntil(this.destroyed$))
        .subscribe((generator) => {
          this.generator = generator;
        });
    }
    super.ngOnInit();
  }

  ngOnChanges(): void {
    if (
      this.wasteProfile &&
      SpecialWasteProfileModel.isSpecialWaste(this.wasteProfile) &&
      this.form
    ) {
      const approvedDisposalConsideration = this.form.get(
        'approvedDisposalConsideration'
      ).value;

      if (!approvedDisposalConsideration) {
        const disposalConsideration = this.wasteProfile.disposalConsideration;
        this.form.patchValue({
          approvedDisposalConsideration: disposalConsideration,
        });
      }
    }
  }

  onMetadataCharacteristicsChange = () => {
    const metadataCharacteristicsGroup = this.form.get(
      'metadataCharacteristics'
    ) as FormGroup<
      ControlsOf<{
        wetWaste: boolean;
        sulfur: boolean;
        sulfurPpm: string;
        pfas: boolean;
        pfasPpb: string;
        heatGeneration: boolean;
        heatGenerationJg: string;
        heatGenerationMetalOxide: string;
        pcbs: boolean;
        norm: boolean;
        tenorm: boolean;
        texasClass1: boolean;
        californiaHazardous: boolean;
      }>
    >;

    metadataCharacteristicsGroup.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (!value.sulfur) {
          metadataCharacteristicsGroup
            .get('sulfurPpm')
            .setValue('', { emitEvent: false });
        }
        if (!value.pfas) {
          metadataCharacteristicsGroup
            .get('pfasPpb')
            .setValue('', { emitEvent: false });
        }
        if (!value.heatGeneration) {
          metadataCharacteristicsGroup
            .get('heatGenerationJg')
            .setValue('', { emitEvent: false });
          metadataCharacteristicsGroup
            .get('heatGenerationMetalOxide')
            .setValue('', { emitEvent: false });
        }
      });
  };

  private getAttachmentsData(value) {
    if (this.formState) {
      value = { ...value, ...this.formState };
    }
    return value;
  }

  public mapInputValue(value: InputData): ValuesOf<MetadataFormControls> {
    const { salesRepName, salesRepNumber, ...data } = value;
    value = this.getAttachmentsData(value);

    let salesRep: MetatdataFormElements['salesRep'] | null = null;

    if (salesRepName) {
      salesRep = {
        name: salesRepName,
        number: salesRepNumber,
      };
    }

    return {
      ...data,
      salesRep,
      destination: {
        landfillname: value?.destination?.name,
        divisionNumber: value?.destination?.divisionNumber,
      },
      generator: {
        name: value?.generator?.name,
        generatorAddress: value?.generator?.siteAddress?.addressLine1,
        city: value?.generator?.siteAddress?.city,
        state: value?.generator?.siteAddress?.stateCode,
      },
      labAnalyticalAttachments: value?.labAnalyticalAttachments || [],
      safetyDataSheets: value?.safetyDataSheets || [],
      additionalSupplements: value?.additionalSupplements || [],
      thirdPartyAuthAdminAttachment: value?.thirdPartyAuthAdminAttachment,
      stateInternalAdminAttachments: value?.stateInternalAdminAttachments,
      internalAdminAttachments: value?.internalAdminAttachments,
      additionalAdminSupplements: [],
    };
  }

  public mapOutputValue(value: ValuesOf<MetadataFormControls>): InputData {
    const {
      salesRep,
      approvedDisposalConsideration,
      destination, // unchangeable in this form
      generator, // unchangeable in this form
      ...data
    } = value;

    if (
      this.wasteProfile &&
      SpecialWasteProfileModel.isSpecialWaste(this.wasteProfile)
    ) {
      return {
        ...this.wasteProfile,
        ...data,
        approvedDisposalConsideration,
        generator: { ...this.wasteProfile?.generator, name: generator?.name },
        salesRepName: salesRep?.name || '',
        salesRepNumber: salesRep?.number || '',
      };
    } else {
      return {
        ...this.wasteProfile,
        ...data,
        generator: { ...this.wasteProfile?.generator, name: generator?.name },
        salesRepName: salesRep?.name || '',
        salesRepNumber: salesRep?.number || '',
      };
    }
  }

  getSignNowDocuments = () => {
    if (this.value?.signNowDocument || this.value?.signNowDocUrl) {
      let profileDocument = this.value?.signNowDocument;
      if (!profileDocument.name) {
        profileDocument = {
          ...profileDocument,
          name: 'Signed Document',
        };
      }

      if (!profileDocument.url && this.value?.signNowDocUrl) {
        profileDocument = {
          ...profileDocument,
          url: this.value?.signNowDocUrl,
        };
      }

      this.signNowDocuments.push({
        ...profileDocument,
        name: `Waste Profile: ${profileDocument.name}`,
      });
    }

    if (this.value?.pendingChangeRequest) {
      const { pendingChangeRequest } = this.value;

      if (
        pendingChangeRequest.isSigned ||
        pendingChangeRequest.signNowDocument?.isComplete
      ) {
        const { signNowDocumentId, signNowDocument: changeDocument } =
          pendingChangeRequest;

        if (
          signNowDocumentId &&
          changeDocument?.id &&
          signNowDocumentId !== changeDocument?.id
        ) {
          // there's an older record and it's different than the other document, show it
          const name =
            pendingChangeRequest?.signNowDocumentName ||
            'Signed Change Request';

          this.signNowDocuments.push({
            id: pendingChangeRequest?.signNowDocumentId,
            url: pendingChangeRequest?.signNowDocumentUrl,
            name: `Change Request: ${name}`,
            isComplete: null,
          });
        }

        if (pendingChangeRequest?.signNowDocument?.isComplete) {
          const name = changeDocument.name || 'Signed Change Request';

          this.signNowDocuments.push({
            ...changeDocument,
            name: `Change Request: ${name}`,
          });
        }
      }
    }

    if (this.value?.pendingRecertification?.signNowDocument?.isComplete) {
      const signNowDocument =
        this.value?.pendingRecertification?.signNowDocument;
      const name = signNowDocument.name || 'Signed Recertification';
      this.signNowDocuments.push({
        ...signNowDocument,
        name: `Recertification: ${name}`,
      });
    }
  };

  getAttachmentUrls = () => {
    this.labAttachments = [
      ...this.getAttachments(this.value?.labAnalyticalAttachments),
      ...this.getAttachments(
        this.value?.pendingRecertification?.labAnalyticalAttachments
      ),
      ...this.getAttachments(
        this.value?.pendingChangeRequest?.labAnalyticalAttachments
      ),
    ];

    this.safetyDataSheets = [
      ...this.getAttachments(this.value?.safetyDataSheets),
      ...this.getAttachments(
        this.value?.pendingRecertification?.safetyDataSheets
      ),
      ...this.getAttachments(
        this.value?.pendingChangeRequest?.safetyDataSheets
      ),
    ];

    this.additionalSupplements = [
      ...this.getAttachments(this.value?.additionalSupplements),
      ...this.getAttachments(
        this.value?.pendingRecertification?.additionalSupplements
      ),
      ...this.getAttachments(
        this.value?.pendingChangeRequest?.additionalSupplements
      ),
    ];
  };

  getAttachments(
    attachments:
      | LabAnalyticalAttachment[]
      | AdditionalSupplement[]
      | SafetyDataSheet[]
  ) {
    return (
      attachments?.map((a) => a?.attachment).filter((f) => f?.fileName) || []
    );
  }

  getOptionsForInternalStatus() {
    let status;
    if (this.wasteProfile?.log?.length > 0) {
      status = this.states.find(
        (i) => i.state === this.form.value.internalStatus
      );
    } else {
      status = this.states.find(
        (i) => i.state === this.wasteProfile?.internalStatus
      );
    }
    if (status) {
      this.internalStatusOptions = status.options;
    } else {
      const allInternalStatus = [
        InternalStatus.ANALYST_APPROVED,
        InternalStatus.ANALYST_REVIEW,
        InternalStatus.COORDINATOR_APPROVED,
        InternalStatus.COORDINATOR_REVIEW,
        InternalStatus.FINAL_APPROVAL,
        InternalStatus.PENDING_CUSTOMER_INFO,
        InternalStatus.RECEIVED,
        InternalStatus.REJECTED,
      ];
      this.internalStatusOptions = this.isAnalyst
        ? allInternalStatus
        : [this.form.value.internalStatus];
    }
  }

  errorHandling = (controlName: keyof MetatdataFormElements, error: string) => {
    const control = this.form.controls[controlName];
    return control.touched && control.hasError(error);
  };

  generatorErrorHandling = (control: string, error: string) =>
    this.form.controls.generator.controls[control].touched &&
    this.form.controls.generator.controls[control].hasError(error);

  onDisposalConsideration = () => {
    const disposalConsiderationsControl =
      this.form.controls.approvedDisposalConsideration;
    const transferFacilityControl =
      this.form.controls.isTransferFacilityRequired;

    const updateValidationState = (
      val: SpecialWasteProfile['approvedDisposalConsideration']
    ) => {
      if (val === 'landfill') {
        transferFacilityControl.enable();
      } else {
        transferFacilityControl.disable();
        transferFacilityControl.setValue(false);
      }

      transferFacilityControl.updateValueAndValidity();
    };

    updateValidationState(disposalConsiderationsControl.value);
    disposalConsiderationsControl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(updateValidationState);
  };

  onFocusOutEvent(f) {
    let updatedValue;
    const logbookSequenceNumberControl =
      this.form.controls.logbookSequenceNumber;
    const status = this.form.controls.internalStatus?.value;
    let defaultValidators = [Validators.pattern('^([1-9][0-9]{0,3})$')];

    if (
      status === InternalStatus.COORDINATOR_APPROVED ||
      status === InternalStatus.ANALYST_APPROVED ||
      status === InternalStatus.FINAL_APPROVAL ||
      status === InternalStatus.REJECTED
    ) {
      defaultValidators = [...defaultValidators, ...[Validators.required]];
    }
    logbookSequenceNumberControl.setValidators(defaultValidators);

    const value = logbookSequenceNumberControl?.value;
    const num = _.toNumber(value);

    if (value && _.isNumber(num) && num > 0 && num <= 99999) {
      const length = value.toString().length;

      if (length === 1) updatedValue = `000${value}`;
      if (length === 2) updatedValue = `00${value}`;
      if (length === 3) updatedValue = `0${value}`;
      if (length === 4 || length === 5) updatedValue = value;

      if (updatedValue) {
        logbookSequenceNumberControl.clearValidators();
        logbookSequenceNumberControl.setValue(updatedValue);
      }
    }
    logbookSequenceNumberControl.updateValueAndValidity();
  }

  public get additionalAdminSupplements() {
    return this.form.get('additionalAdminSupplements');
  }

  public get stateInternalAdminAttachments() {
    return this.form.get('stateInternalAdminAttachments');
  }

  public get internalAdminAttachments() {
    return this.form.get('internalAdminAttachments');
  }

  public addAnotherAdditionalSupplements() {
    this.showAnotherAdditionalAdminSupplements = false;
    this.additionalAdminSupplements.push(this.fb.control(null) as any);
  }
  public addAnotherStateAdminattachments() {
    this.showAnotherAdminStateAttachments = false;
    this.stateInternalAdminAttachments.push(this.fb.control(null) as any);
  }
  public addAnotherInternalOnlyAttachment() {
    this.showAnotherInternalOnlyAttachment = false;
    this.internalAdminAttachments.push(this.fb.control(null) as any);
  }

  public deleteAttachmentLog(wp: SpecialWasteProfile) {
    //We perform the removal and cleaning in the log component
    //so we just take in a cleaned wp here ^
    this.formState = _.clone({
      ...this.form.value,
      wp,
    });
    //update the wp
    this.facade.patch({
      ...wp,
    });
    //rehydrate the fields
    this.form.patchValue(this.formState);
  }

  public onAdditionalAdminSupplementDeleted(fileData) {
    const { additionalAdminSupplements: asPrev } = this.wasteProfile;
    const additionalAdminSupplements = asPrev.filter(
      (as) => as?.attachment.id !== fileData.id
    );
    this.wasteProfile = {
      ...this.wasteProfile,
      additionalAdminSupplements,
    };
    this.formState = _.clone({
      ...this.form.value,
      additionalAdminSupplements,
    });
    this.facade.patch({
      ...this.wasteProfile,
      additionalAdminSupplements,
    });
    this.form.patchValue(this.formState);
    this.attachmentsToSave = false;
  }

  public onInternalOnlyAttachmentDeleted(fileData) {
    const { internalAdminAttachments: asPrev } = this.wasteProfile;
    const internalAdminAttachments = asPrev.filter(
      (ia) => ia.id !== fileData.id
    );
    this.wasteProfile = {
      ...this.wasteProfile,
      internalAdminAttachments,
    };
    this.formState = _.clone({
      ...this.form.value,
      internalAdminAttachments,
    });
    this.facade.patch({
      ...this.wasteProfile,
      internalAdminAttachments,
    });
    this.form.patchValue(this.formState);
    this.attachmentsToSave = false;
  }

  public onThirdPartyAttachmentDeleted(fileData) {
    if (fileData?.fileUrl) {
      this.wasteProfile = {
        ...this.wasteProfile,
        thirdPartyAuthAdminAttachment: null,
      };
      this.formState = _.clone({
        ...this.form.value,
        thirdPartyAuthAdminAttachment: null,
      });
      this.facade.patch({
        ...this.wasteProfile,
        thirdPartyAuthAdminAttachment: null,
      });
      this.form.patchValue(this.formState);
      this.attachmentsToSave = false;
    }
  }

  public onStateAdminAttachmentDeleted(fileData: Attachment) {
    const { stateInternalAdminAttachments: saPrev } = this.wasteProfile;
    const stateInternalAdminAttachments = saPrev.filter(
      (sa) => sa.id !== fileData.id
    );
    this.wasteProfile = {
      ...this.wasteProfile,
      stateInternalAdminAttachments,
    };
    this.formState = _.clone({
      ...this.form.value,
      stateInternalAdminAttachments,
    });
    this.facade.patch({
      ...this.wasteProfile,
      stateInternalAdminAttachments,
    });
    this.form.patchValue(this.formState);
    this.attachmentsToSave = false;
  }

  public onAdditionalAdminSupplementUpdate() {
    this.form.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        value.additionalAdminSupplements.forEach((attachment, index, list) => {
          if (attachment !== null) {
            this.attachmentsToSave = true;
          }
          if (attachment === null && index + 1 !== list.length) {
            this.additionalAdminSupplements.removeAt(index);
          }
        });
      });
    this.form.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        value.stateInternalAdminAttachments.forEach(
          (attachment, index, list) => {
            if (attachment !== null) {
              this.attachmentsToSave = true;
            }
            if (attachment === null && index + 1 !== list.length) {
              this.stateInternalAdminAttachments.removeAt(index);
            }
          }
        );
      });
    this.form.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        value.internalAdminAttachments.forEach((attachment, index, list) => {
          if (attachment !== null) {
            this.attachmentsToSave = true;
          }
          if (attachment === null && index + 1 !== list.length) {
            this.internalAdminAttachments.removeAt(index);
          }
        });
      });

    this.form.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe((value) => {
        if (value.thirdPartyAuthAdminAttachment?.fileUrl) {
          this.attachmentsToSave = true;
        }
      });

    // update the flag if we should allow another to be added
    this.form.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        throttleTime(50, undefined, { leading: false, trailing: true })
      )
      .subscribe((value) => {
        this.showAnotherAdditionalAdminSupplements =
          value.additionalAdminSupplements.reduce(
            (acc, attachment) => acc && attachment !== null,
            true
          ) as any;

        this.showAnotherAdminStateAttachments =
          value?.stateInternalAdminAttachments.reduce(
            (acc, attachment) => acc && attachment !== null,
            true
          ) as any;

        this.showAnotherInternalOnlyAttachment =
          value.internalAdminAttachments.reduce(
            (acc, attachment) => acc && attachment !== null,
            true
          ) as any;
      });
  }

  resetWasteProfileErrorState() {
    this.isValidWasteProfileNumber = true;
    this.checkWasteProfileNumberIsValid.emit(true);
  }

  onSubmit() {
    this.saveAttachments.emit();
    setTimeout(() => {
      this.attachmentsToSave = false;
    });
  }

  setStates() {
    // had a request that analysts get access to ALL statuses, all the time
    const allInternalStatus = [
      InternalStatus.ANALYST_APPROVED,
      InternalStatus.ANALYST_REVIEW,
      InternalStatus.COORDINATOR_APPROVED,
      InternalStatus.COORDINATOR_REVIEW,
      InternalStatus.FINAL_APPROVAL,
      InternalStatus.PENDING_CUSTOMER_INFO,
      InternalStatus.RECEIVED,
      InternalStatus.REJECTED,
    ];
    this.states = [
      {
        state: InternalStatus.RECEIVED,
        options: this.isAnalyst
          ? allInternalStatus
          : [
              InternalStatus.RECEIVED,
              InternalStatus.COORDINATOR_REVIEW,
              InternalStatus.COORDINATOR_APPROVED,
              InternalStatus.PENDING_CUSTOMER_INFO,
            ],
      },
      {
        state: InternalStatus.COORDINATOR_REVIEW,
        options: this.isAnalyst
          ? allInternalStatus
          : [
              InternalStatus.COORDINATOR_REVIEW,
              InternalStatus.COORDINATOR_APPROVED,
              InternalStatus.PENDING_CUSTOMER_INFO,
            ],
      },
      {
        state: InternalStatus.COORDINATOR_APPROVED,
        options: this.isAnalyst
          ? allInternalStatus
          : [
              InternalStatus.COORDINATOR_APPROVED,
              InternalStatus.PENDING_CUSTOMER_INFO,
            ],
      },
      {
        state: InternalStatus.ANALYST_REVIEW,
        options: this.isAnalyst
          ? allInternalStatus
          : [
              InternalStatus.ANALYST_REVIEW,
              InternalStatus.PENDING_CUSTOMER_INFO,
              InternalStatus.REJECTED,
            ],
      },
      {
        state: InternalStatus.ANALYST_APPROVED,
        options: this.isAnalyst
          ? allInternalStatus
          : [
              InternalStatus.ANALYST_APPROVED,
              InternalStatus.FINAL_APPROVAL,
              InternalStatus.REJECTED,
            ],
      },
    ];

    if (this.wasteProfile?.log?.length > 1) {
      this.states.push({
        state: InternalStatus.PENDING_CUSTOMER_INFO,
        options: this.isAnalyst
          ? allInternalStatus
          : [InternalStatus.PENDING_CUSTOMER_INFO, InternalStatus.RECEIVED],
      });
    }
  }

  updateGenerator() {
    if (!this.disableGeneratorFields && this.generator) {
      this.generator.siteAddress.addressLine1 =
        this.form.value.generator.generatorAddress;
      this.generator.siteAddress.city = this.form.value.generator.city;
      this.generator.siteAddress.stateCode = this.form.value.generator.state;
      this.generatorsFacade.upsert(this.generator);
    }
  }

  openActiveWasteProfileModal(active) {
    if (active) {
      this.activeTitle = 'Active';
    } else {
      this.activeTitle = 'Inactive';
    }
    this.dialog.open(this.activeWasteProfileDialog, {
      width: '650px',
    });
  }

  deny() {
    const activeValue = this.form.get('active').value;

    if (activeValue) {
      this.form.controls.active.setValue(false);
    } else {
      this.form.controls.active.setValue(true);
    }

    this.dialog?.closeAll();
  }
  confirm() {
    this.submitActivate.emit(true);
    this.dialog?.closeAll();
  }
}
